import * as DialogPrimitive from '@radix-ui/react-dialog';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import {
  ArrowRightLeft,
  Calendar,
  CircleAlert,
  CircleDot,
  Circle,
  Feather,
  MessageSquare,
  Zap,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/Config/app/i18n';
import { CampaignDto } from '@/Domain/api/Action/dto/Campaign.dto';
import { ParameterList } from '@/Domain/components/Action/CampaignAction/Parameter/ParameterList';
import {
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { BoxIcon } from '@/Domain/components/Shared/BoxIcon/BoxIcon';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Field, FieldDescription, FieldLabel } from '@/Domain/components/Shared/Field/Field';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { RadioGroup, RadioGroupItem } from '@/Domain/components/Shared/RadioGroup/RadioGroup';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { TooltipProvider } from '@/Domain/components/Shared/Tooltip/Tooltip';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { useCampaignActionForm } from '@/Domain/hooks/Action/CampaignAction/useCampaignActionForm';
import { useActiveSection } from '@/Domain/hooks/App/useActiveSection.hook';
import { CAL_LINK, useCalModal } from '@/Domain/hooks/useCalModal.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

type Props = {
  campaign: CampaignDto;
};

export const CampaignSheet = ({ campaign }: Props) => {
  const { t } = useTranslation('campaigns');
  const { isOpen, closeModal } = useModalStore();
  const openCalModal = useCalModal();

  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [scrollContainer, setScrollContainer] = useState<HTMLElement | null>(null);
  const [indicatorTop, setIndicatorTop] = useState(0);

  const { form, handleSubmit } = useCampaignActionForm(campaign);
  const {
    formState: { isDirty },
  } = form;
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const sections = useMemo(
    () => [
      { id: 'name', label: i18n.t('campaigns:menu.name') },
      { id: 'description', label: i18n.t('campaigns:menu.description') },
      { id: 'data-to-collect', label: i18n.t('campaigns:menu.data_to_collect') },
      { id: 'reason_for_gathering', label: i18n.t('campaigns:menu.reason_for_gathering') },
      { id: 'when_to_ask_data', label: i18n.t('campaigns:menu.when_to_ask') },
      { id: 'how-to-ask', label: i18n.t('campaigns:menu.how_to_ask') },
      { id: 'where_to_send_data', label: i18n.t('campaigns:menu.where_to_send_data') },
      { id: 'need_help', label: i18n.t('campaigns:menu.need_help') },
    ],
    [t],
  );

  const handleRequestClose = () => {
    if (isDirty) {
      setConfirmationModalOpen(true);
    } else {
      closeModal();
      form.reset(campaign);
    }
  };

  const handleConfirmClose = () => {
    setConfirmationModalOpen(false);
    closeModal();
    form.reset(campaign);
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleRequestClose();
      }
    },
    [handleRequestClose],
  );

  useEffect(() => {
    if (!isOpen(MODAL_NAMES.CAMPAIGN_SHEET)) return;

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen(MODAL_NAMES.CAMPAIGN_SHEET), handleKeyDown]);

  const handleSheetOpenChange = (open: boolean) => {
    if (!open) handleRequestClose();
  };

  const { activeId, handleScrollToSection } = useActiveSection(
    sections.map((section) => section.id),
    scrollContainer,
  );

  const handleScheduleCall = () => openCalModal(CAL_LINK.productDemo);

  useEffect(() => {
    if (!isOpen(MODAL_NAMES.CAMPAIGN_SHEET)) return;

    const animationFrame = requestAnimationFrame(() => {
      if (scrollContainerRef.current) {
        setScrollContainer(scrollContainerRef.current);
      }
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [isOpen(MODAL_NAMES.CAMPAIGN_SHEET)]);

  useEffect(() => {
    const updateIndicator = () => {
      const container = containerRef.current;
      if (!container) return;

      const activeLink = container.querySelector(`[data-id="${activeId}"]`);
      if (activeLink instanceof HTMLElement) {
        const offset = activeLink.offsetTop + activeLink.offsetHeight - 36;
        setIndicatorTop(offset);
      }
    };

    updateIndicator();

    window.addEventListener('resize', updateIndicator);

    return () => {
      window.removeEventListener('resize', updateIndicator);
    };
  }, [activeId]);

  if (!campaign) {
    return null;
  }

  return (
    <TooltipProvider>
      <Form {...form}>
        <Sheet open={isOpen(MODAL_NAMES.CAMPAIGN_SHEET)} onOpenChange={handleSheetOpenChange}>
          <SheetContent
            className="!max-w-[100vw] xl:!w-[85%] xl:!max-w-7xl w-full pb-0 space-y-0"
            tabIndex={undefined}
            asChild
          >
            <form onSubmit={form.handleSubmit(handleSubmit)} className="h-full">
              <SheetHeader className="!flex-row !items-center !justify-between">
                <SheetTitle>{t('title')}</SheetTitle>
                <section className="flex items-center gap-2">
                  <Button type="submit">{t('button.save')}</Button>
                  <IconButton onClick={() => handleSheetOpenChange(false)} variant="secondary">
                    <CloseIcon />
                  </IconButton>
                </section>
              </SheetHeader>

              <div className="grid grid-cols-1 xl:grid-cols-[0.2fr_auto_1fr] h-full overflow-hidden">
                <nav ref={containerRef} className="hidden xl:flex relative flex-col">
                  <motion.span
                    layout
                    layoutId="nav-indicator"
                    className="absolute left-0 w-[3px] h-[36px] bg-foreground"
                    style={{ top: indicatorTop }}
                    transition={{ type: 'tween', stiffness: 200, damping: 30 }}
                  />

                  <p className="text-foreground-subtle text-xs font-semibold mb-5">
                    {t('menu.overview')}
                  </p>

                  {sections.map(({ id, label }) => (
                    <a
                      key={id}
                      data-id={id}
                      onClick={() => handleScrollToSection(id)}
                      className={classNames(
                        'border-l border-border h-[36px] flex items-center relative text-sm text-left pl-4 cursor-pointer transition-colors duration-300 !no-underline',
                        {
                          'text-foreground font-semibold': activeId === id,
                          'text-muted-foreground font-medium': activeId !== id,
                        },
                      )}
                    >
                      {label}
                    </a>
                  ))}
                </nav>

                <Separator orientation="vertical" className="hidden xl:flex mr-4" />

                <section
                  ref={scrollContainerRef}
                  className="overflow-y-auto space-y-7 custom-scroll relative pl-1 pr-1"
                >
                  <div id="name">
                    <FormField
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('form.name_label')}</FormLabel>
                          <FormControl>
                            <Input autoFocus placeholder={t('form.name_placeholder')} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator />

                  <div id="description">
                    <FormField
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('form.description_label')}</FormLabel>
                          <FormControl>
                            <Input placeholder={t('form.description_placeholder')} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator />

                  <ParameterList form={form} />

                  <Separator />

                  <div id="reason_for_gathering">
                    <FormField
                      name="reason_for_gathering"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('form.reason_for_collecting_label')}</FormLabel>
                          <FormDescription>
                            {t('form.reason_for_collecting_description')}
                          </FormDescription>
                          <FormControl>
                            <Input
                              placeholder={t('form.reason_for_collecting_placeholder')}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator />

                  <div id="when_to_ask_data">
                    <FormField
                      name="question_timing"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('form.when_to_ask_data_label')}</FormLabel>
                          <FormDescription className="mb-5">
                            {t('form.when_to_ask_data_description')}
                          </FormDescription>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              value={field.value}
                              className="grid grid-cols-2 gap-3 items-center"
                            >
                              <RadioGroupItem
                                id="short_delay"
                                value="short_delay"
                                className="peer sr-only"
                              />
                              <label
                                htmlFor="short_delay"
                                className={classNames(
                                  'p-4 border border-border rounded-lg flex items-center  justify-between cursor-pointer',
                                  {
                                    '!border-foreground': field.value === 'short_delay',
                                  },
                                )}
                              >
                                <div className="flex items-center gap-3 text-sm">
                                  <BoxIcon variant="gray">
                                    <MessageSquare />
                                  </BoxIcon>
                                  {t('form.when_to_ask.after_messages')}
                                </div>
                                {field.value === 'short_delay' ? (
                                  <CircleDot className="size-4" />
                                ) : (
                                  <Circle className="size-4 text-foreground-subtle" />
                                )}
                              </label>

                              <RadioGroupItem
                                value="immediate"
                                id="immediate"
                                className="peer sr-only"
                              />
                              <label
                                htmlFor="immediate"
                                className={classNames(
                                  'p-4 border border-border rounded-lg flex items-center  justify-between cursor-pointer',
                                  {
                                    '!border-foreground': field.value === 'immediate',
                                  },
                                )}
                              >
                                <div className="flex items-center gap-3 text-sm">
                                  <BoxIcon variant="gray">
                                    <Zap />
                                  </BoxIcon>
                                  {t('form.when_to_ask.immediately')}
                                </div>

                                {field.value === 'immediate' ? (
                                  <CircleDot className="size-4" />
                                ) : (
                                  <Circle className="size-4 text-foreground-subtle" />
                                )}
                              </label>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator />

                  <div id="how-to-ask">
                    <FormField
                      name="question_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('form.how_to_ask_data_label')}</FormLabel>
                          <FormDescription className="mb-5">
                            {t('form.how_to_ask_data_description')}
                          </FormDescription>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              value={field.value}
                              className="grid grid-cols-2 gap-3 items-center"
                            >
                              <RadioGroupItem id="subtle" value="subtle" className="peer sr-only" />
                              <label
                                htmlFor="subtle"
                                className={classNames(
                                  'p-4 border border-border rounded-lg flex items-center  justify-between cursor-pointer',
                                  {
                                    '!border-foreground': field.value === 'subtle',
                                  },
                                )}
                              >
                                <div className="flex items-center gap-3 text-sm">
                                  <BoxIcon variant="gray">
                                    <Feather />
                                  </BoxIcon>
                                  {t('form.how_to_ask.subtle')}
                                </div>

                                {field.value === 'subtle' ? (
                                  <CircleDot className="size-4" />
                                ) : (
                                  <Circle className="size-4 text-foreground-subtle" />
                                )}
                              </label>

                              <RadioGroupItem value="direct" id="direct" className="peer sr-only" />
                              <label
                                htmlFor="direct"
                                className={classNames(
                                  'p-4 border border-border rounded-lg flex items-center justify-between cursor-pointer',
                                  {
                                    '!border-foreground': field.value === 'direct',
                                  },
                                )}
                              >
                                <div className="flex items-center gap-3 text-sm">
                                  <BoxIcon variant="gray">
                                    <ArrowRightLeft />
                                  </BoxIcon>
                                  {t('form.how_to_ask.direct')}
                                </div>

                                {field.value === 'direct' ? (
                                  <CircleDot className="size-4" />
                                ) : (
                                  <Circle className="size-4 text-foreground-subtle" />
                                )}
                              </label>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator />
                  <div id="where_to_send_data">
                    <Field>
                      <FieldLabel>{t('form.where_to_send_data_label')}</FieldLabel>
                      <FieldDescription>{t('form.where_to_send_data_subtitle')}</FieldDescription>
                    </Field>
                    <Button variant="secondary" onClick={handleScheduleCall} className="mt-4">
                      <Calendar className="size-4" />
                      {t('button.schedule_a_call')}
                    </Button>
                  </div>
                  <div
                    id="image"
                    className="flex py-[12vh] xl:py-[26vh] space-y-2 flex-col items-center"
                  >
                    <img
                      src="/static/assets/qc-smart-data.webp"
                      alt="AI Insights graphic"
                      className="w-64 h-64 object-cover"
                    />
                    <p className="font-medium">{t('form.data_gathering_image')}</p>
                    <p
                      className="text-sm text-foreground-subtle text-center max-w-xl"
                      dangerouslySetInnerHTML={{
                        __html: t('form.data_gathering_image_description', {
                          link: '<a href="https://docs.quickchat.ai/ai-agent/actions#smart-data-gathering" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">Documentation</a>',
                        }),
                      }}
                    ></p>
                  </div>
                </section>
              </div>

              <DialogPrimitive.Root
                open={confirmationModalOpen}
                onOpenChange={setConfirmationModalOpen}
              >
                <DialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 backdrop-blur-sm z-20" />

                <DialogPrimitive.Content
                  className="absolute inset-0 flex items-center justify-center p-0 z-30 focus:outline-none"
                  role="alertdialog"
                  aria-modal="true"
                >
                  <div className="space-y-4 bg-card rounded-lg p-6 max-w-md w-full shadow-lg">
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        <CircleAlert className="inline bg-red-100 rounded-lg text-red-500 p-2 box-content size-4" />
                        {t('close_modal.title')}
                      </AlertDialogTitle>
                    </AlertDialogHeader>

                    <AlertDialogDescription>{t('close_modal.subtitle')}</AlertDialogDescription>

                    <Separator className="my-7" />
                    <AlertDialogFooter>
                      <AlertDialogCancel asChild>
                        <Button size="md" variant="secondary">
                          {t('close_modal.button.cancel')}
                        </Button>
                      </AlertDialogCancel>

                      <Button variant="danger" size="md" onClick={handleConfirmClose}>
                        {t('close_modal.button.continue')}
                      </Button>
                    </AlertDialogFooter>
                  </div>
                </DialogPrimitive.Content>
              </DialogPrimitive.Root>
            </form>
          </SheetContent>
        </Sheet>
      </Form>
    </TooltipProvider>
  );
};
